import { ChevronUpDownIcon, FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useId, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { termLabel } from "../../../api/content/processTermWords";
import type { AttachedFilter, SearchIn } from "../../../api/content/searchApi";
import { iriLocalName } from "../../../shared/references/shortReferences";
import AppButton from "../../../ui/buttons/AppButton";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Field from "../../../ui/forms/Field";
import OptionLabel from "../../../ui/forms/OptionLabel";
import SuggestionList from "../../../ui/forms/SuggestionList";
import { nextSuggestionIndex, suggestionOptionId } from "../../../ui/forms/suggestionNavigation";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import {
  facetsIn,
  findFacets,
  kindChoiceOf,
  withAcceptedOnly,
  withFacet,
  withKindChoice,
  withoutFacet,
  type AttachedFacet,
  type KindChoice,
} from "../attached/attachedFilter";
import { useAttachedChoices, type AttachedFilterChoices } from "../attached/useAttachedChoices";
import { filterCount, labelSegmentType, NO_SEARCH_FILTERS, type SearchFilters } from "./searchFilters";

const ANY = "any";
const NOTHING = "nothing";
/** "Search in": a kind of segment is its name; neither value fits a name's pattern (`segmentation.py`). */
const WHOLE_DOCUMENT = "";
const STATEMENTS = ":statements";

interface SearchFilterControlsProps {
  projectId: string;
  filters: SearchFilters;
  /** The kinds of segment the corpora searched declare (F141); without any, no such choice. */
  segmentTypes: readonly string[];
  onFiltersChange: (filters: SearchFilters) => void;
}

/**
 * The search's filters behind one button that counts them (F88, F91, F141; Simeon's expert
 * search): the kind of segment searched, which kind of data a document has (or none), accepted
 * data only, and the labels and model terms its data uses, chosen from a list that opens with
 * the field. Kinds are named by the DMW process ontology; segment types as the corpus names them.
 */
export default function SearchFilterControls({ projectId, filters, segmentTypes, onFiltersChange }: SearchFilterControlsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const count = filterCount(filters);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <AppButton size="xs" variant="secondary" className="gap-1" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => { setOpen(!open); }}>
          <FunnelIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {t("search.attached.filters")}
          {count === 0 ? null : <Chip size="compact" tone="info">{count}</Chip>}
        </AppButton>
        {count === 0 ? null : (
          <AppButton size="xs" variant="ghost" onClick={() => { onFiltersChange(NO_SEARCH_FILTERS); }}>
            {t("search.attached.clear")}
          </AppButton>
        )}
      </div>
      {open ? <FilterPanel id={panelId} projectId={projectId} filters={filters} segmentTypes={segmentTypes} onFiltersChange={onFiltersChange} /> : null}
    </div>
  );
}

function FilterPanel({ id, projectId, filters, segmentTypes, onFiltersChange }: SearchFilterControlsProps & { id: string }) {
  const { t } = useTranslation();
  const { state, reload } = useAttachedChoices(projectId);
  const changeAttached = (attached: AttachedFilter) => { onFiltersChange({ ...filters, attached }); };
  // Statements are offered only where documents have some to search, or while they are searched.
  const offersStatements = filters.searchIn.kind === "statements" || (state.status === "loaded" && state.value.statedDocuments > 0);
  return (
    <div id={id} role="group" aria-label={t("search.attached.filters")} className="app-surface space-y-3 rounded border p-2">
      {segmentTypes.length === 0 && !offersStatements ? null : (
        <SearchInField segmentTypes={segmentTypes} offersStatements={offersStatements} searchIn={filters.searchIn} onChange={(searchIn) => { onFiltersChange({ ...filters, searchIn }); }} />
      )}
      {state.status !== "loaded" ? (
        <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact />
      ) : (
        <>
          <KindField choices={state.value} filter={filters.attached} onFilterChange={changeAttached} />
          <OptionLabel size="small">
            <input type="checkbox" checked={filters.attached.acceptedOnly} onChange={(event) => { changeAttached(withAcceptedOnly(filters.attached, event.target.checked)); }} />
            {t("search.attached.acceptedOnly")}
          </OptionLabel>
          {filters.attached.kind === "nothing" ? null : (
            <FacetChooser facets={state.value.facets} segmentType={labelSegmentType(filters)} filter={filters.attached} onFilterChange={changeAttached} />
          )}
        </>
      )}
    </div>
  );
}

interface SearchInFieldProps {
  segmentTypes: readonly string[];
  offersStatements: boolean;
  searchIn: SearchIn;
  onChange: (searchIn: SearchIn) => void;
}

/**
 * Where the words are searched (F141): the whole document, one kind of segment such as the head,
 * or what the statements attached to the documents say (Simeon's "Search T-/A-boxes").
 */
function SearchInField({ segmentTypes, offersStatements, searchIn, onChange }: SearchInFieldProps) {
  const { t } = useTranslation();
  const chosen = searchIn.kind === "statements" ? STATEMENTS : (searchIn.segmentType ?? WHOLE_DOCUMENT);
  const choose = (value: string) => {
    onChange(value === STATEMENTS ? { kind: "statements" } : { kind: "text", segmentType: value === WHOLE_DOCUMENT ? null : value });
  };
  return (
    <Field label={t("search.filters.segment")}>
      {(control) => (
        <select {...control} data-filter="segment" value={chosen} className={FORM_COMPACT_CONTROL_CLASS} onChange={(event) => { choose(event.target.value); }}>
          <option value={WHOLE_DOCUMENT}>{t("search.filters.wholeDocument")}</option>
          {segmentTypes.map((each) => <option key={each} value={each}>{each}</option>)}
          {offersStatements ? <option value={STATEMENTS}>{t("search.filters.statements")}</option> : null}
        </select>
      )}
    </Field>
  );
}

function KindField({ choices, filter, onFilterChange }: { choices: AttachedFilterChoices; filter: AttachedFilter; onFilterChange: (filter: AttachedFilter) => void }) {
  const { t } = useTranslation();
  const choice = kindChoiceOf(filter);
  const value = choice.kind === "class" ? choice.iri : choice.kind;
  const change = (selected: string) => {
    const next: KindChoice = selected === ANY ? { kind: "any" } : selected === NOTHING ? { kind: "nothing" } : { kind: "class", iri: selected };
    onFilterChange(withKindChoice(filter, next));
  };
  return (
    <Field label={t("search.attached.kind")}>
      {(control) => (
        <select {...control} value={value} className={FORM_COMPACT_CONTROL_CLASS} onChange={(event) => { change(event.target.value); }}>
          <option value={ANY}>{t("search.attached.anyKind")}</option>
          {choices.kinds.map((kind) => <option key={kind} value={kind}>{termLabel(kind, t) ?? iriLocalName(kind)}</option>)}
          <option value={NOTHING}>{t("search.attached.nothing")}</option>
        </select>
      )}
    </Field>
  );
}

/**
 * Choose a label or term (F141; WAI-ARIA APG combobox, W-10's one suggestion list): a field that
 * reads as a dropdown and lists every choice as soon as it has the focus, narrowing them while
 * typing, as a select with a filter does; the chosen ones beneath, each removable. Searched in a
 * kind of segment, it offers what lies there, yet shows every choice that still filters.
 */
function FacetChooser({ facets, segmentType, filter, onFilterChange }: { facets: readonly AttachedFacet[]; segmentType: string | null; filter: AttachedFilter; onFilterChange: (filter: AttachedFilter) => void }) {
  const { t } = useTranslation();
  const listId = useId();
  const [text, setText] = useState("");
  const [listed, setListed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const offered = findFacets(facetsIn(facets, segmentType), filter, text);
  const active = Math.min(activeIndex, Math.max(offered.length - 1, 0));
  const chosen = facets.filter((facet) => filter.kind === "with" && (facet.kind === "label" ? filter.labels : filter.terms).includes(facet.value));
  const choose = (facet: AttachedFacet) => {
    onFilterChange(withFacet(filter, facet));
    setText("");
    setActiveIndex(0);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const next = nextSuggestionIndex(event.key, active, offered.length);
    const picked = offered[active];
    if (next !== null) { setListed(true); setActiveIndex(next); }
    else if (event.key === "Enter" && listed && picked !== undefined) choose(picked);
    else if (event.key === "Escape" && listed) setListed(false);
    else return;
    event.preventDefault();
  };
  return (
    <div className="space-y-2">
      <Field label={t("search.attached.facets")}>
        {(control) => (
          <div className="relative">
            <input
              {...control}
              type="text"
              role="combobox"
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={listed}
              aria-controls={listId}
              aria-activedescendant={!listed || offered.length === 0 ? undefined : suggestionOptionId(listId, active)}
              value={text}
              placeholder={t("search.attached.facetsPlaceholder")}
              className={`${FORM_COMPACT_CONTROL_CLASS} pr-7`}
              onFocus={() => { setListed(true); }}
              onBlur={() => { setListed(false); }}
              onChange={(event) => { setText(event.target.value); setListed(true); setActiveIndex(0); }}
              onKeyDown={onKeyDown}
            />
            <ChevronUpDownIcon className="app-text-muted pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden="true" />
          </div>
        )}
      </Field>
      {!listed ? null : offered.length === 0 ? (
        <EmptyState size="small">{t("search.attached.noFacets")}</EmptyState>
      ) : (
        <SuggestionList
          id={listId}
          label={t("search.attached.facets")}
          items={offered}
          activeIndex={active}
          itemKey={(facet) => `${facet.kind}:${facet.value}`}
          renderItem={(facet) => <FacetRow facet={facet} />}
          onChoose={choose}
          placement="inPanel"
        />
      )}
      {chosen.length === 0 ? null : (
        <ul className="flex flex-wrap gap-1" aria-label={t("search.attached.chosen")}>
          {chosen.map((facet) => (
            <li key={`${facet.kind}:${facet.value}`}>
              <Chip size="small" tone="info" title={facet.kind === "term" ? facet.value : undefined}>
                {facet.name}
                <button type="button" className="app-icon-button -mr-1" aria-label={t("search.attached.remove", { name: facet.name })} onClick={() => { onFilterChange(withoutFacet(filter, facet)); }}>
                  <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                </button>
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FacetRow({ facet }: { facet: AttachedFacet }) {
  const { t } = useTranslation();
  return (
    <span className="flex min-w-0 items-baseline gap-2" title={facet.kind === "term" ? facet.value : undefined}>
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{facet.name}</span>
      <span className="app-text-muted shrink-0 text-xs">{t(facet.kind === "label" ? "search.attached.label" : "search.attached.term")}</span>
      <span className="app-text-muted shrink-0 text-xs tabular-nums">{facet.documents}</span>
    </span>
  );
}
